import { Component, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { AWSCognitoErrorResponse } from '../models/error.model';
import { Action_SetAgentUserObjectToCompleteRegistration, Action_Set_CurrentUserType } from '../state/actions';
import { configureAmplify } from '../config/aws.config';
import { LookupService } from '../services/lookup.service';
import { SingleResultApiResponse } from '../models/api-response.model';
import { AWSSettings } from '../models/aws-settings.model';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';


@Component({
  selector: 'online-app-agent-login',
  templateUrl: './agent-login.component.html',
  styleUrls: ['./agent-login.component.scss']
})
export class AgentLoginComponent implements OnInit, OnDestroy {

  constructor(private fb: FormBuilder,
    private store: Store<any>,
    private authService: AuthService,
    private lookupService: LookupService,
    private renderer: Renderer2,
    private router: Router,
    private titleService: Title) {
    this.titleService.setTitle("Online Application Portal - DE International - Agent");
    this.renderer.addClass(document.body, 'login-screen');
  }

  loginFormGroup: FormGroup;
  isFormSubmitted = false;
  loginErrorMessage = '';

  showNotification: boolean;
  notificationMessage: string;
  phase1Decommissioned: boolean;
    
  private subscriptionsToDispose: Subscription[] = [];

  ngOnInit() {

    this.lookupService.getAWSAuthSettings(true)
      .subscribe((response: SingleResultApiResponse) => {

        if (response.Result) {
          //console.log('getAWSAuthSettings - response.Result');
          //console.log(response.Result);
          const awsSettings: AWSSettings = {
            Region: response.Result.Region,
            UserPoolId: response.Result.UserPoolId,
            UserPoolWebClientId: response.Result.UserPoolWebClientId,
            IdentityPoolId: response.Result.IdentityPoolId,
            AuthenticationFlowType: response.Result.AuthenticationFlowType,
            BucketName: response.Result.BucketName
          };

          localStorage.setItem('awsSettings', JSON.stringify(awsSettings));

          configureAmplify(true);
        }

      }, err => {
        console.log('error happened while calling AWS Auth Settings');
      }, () => {
      });

    this.lookupService.getNotificationSettings(2)
      .subscribe((response: SingleResultApiResponse) => {
        if (response.Result) {
          this.showNotification = response.Result.Item1;
          this.notificationMessage = response.Result.Item2;
          this.phase1Decommissioned = response.Result.Item3;
        }
      }, err => {
        console.log('error happened while calling Notification Settings');
      });

    this.loginFormGroup = this.fb.group({
      loginId: ['', Validators.compose([Validators.required, Validators.email, Validators.minLength(5)])],
      password: ['', Validators.compose([Validators.required])],
    });


    this.subscriptionsToDispose.push(
      this.loginFormGroup.get('loginId').valueChanges.subscribe(values => {
        this.isFormSubmitted = false;
      })
    );

    this.subscriptionsToDispose.push(
      this.loginFormGroup.get('password').valueChanges.subscribe(values => {
        this.isFormSubmitted = false;
      })
    );

  }

  ngOnDestroy() {
    this.subscriptionsToDispose.forEach(s => s.unsubscribe());
    this.renderer.removeClass(document.body, 'login-screen');
  }

  login(): void {

    //We agree for agent email, both Marketing Portal & Online Application use lower case to register / authenticate
    const loginId = !!this.loginFormGroup.get('loginId').value ? this.loginFormGroup.get('loginId').value.trim().toLowerCase() : '';
    const password = this.loginFormGroup.get('password').value;

    
    if (!loginId) {
      this.loginFormGroup.get('loginId').setValue(null);
      this.loginFormGroup.updateValueAndValidity();
    }

    if (!password) {
      this.loginFormGroup.get('password').setValue(null);
      this.loginFormGroup.updateValueAndValidity();
    }
    
    this.isFormSubmitted = true;

    if (this.loginFormGroup.valid) {

      //console.log(localStorage.getItem('awsSettings'));

      this.authService.agentLogin(loginId, password)
        .subscribe((user) => {

          if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {

            this.store.dispatch({
              type: Action_SetAgentUserObjectToCompleteRegistration,
              payload: user
            });

            this.router.navigate(['/agent-set-password']);
          } else {

            this.store.dispatch({
              type: Action_Set_CurrentUserType,
              payload: 'agent'
            });

            localStorage.setItem('userType', 'agent');

            localStorage.setItem('id_token', user.signInUserSession.idToken.jwtToken);

            this.authService.storeCognitoIdentityId(user);

            this.router.navigate(['/applications', 'agent-dashboard']);
          }

        }, (err: AWSCognitoErrorResponse) => {

          //console.log('AWSCognitoErrorResponse-err: ', err);
          if (err.code === 'UserNotConfirmedException') {
            this.loginErrorMessage = 'It seems that you\'ve not confirmed your registration. Please check your email';
            this.loginFormGroup.get('loginId').setErrors({ notConfirmed: true });
          } else if (err.code === 'NotAuthorizedException') {
            this.loginErrorMessage = 'Invalid email address or password';
            this.loginFormGroup.get('loginId').setErrors({ notAuthorized: true });
            this.loginFormGroup.get('password').setErrors({ notAuthorized: true });
          } else {
            this.loginErrorMessage = 'Invalid email address or password';
            this.loginFormGroup.get('loginId').setErrors({ generalUnAuthorized: true });
            this.loginFormGroup.get('password').setErrors({ notAuthorized: true });
          }

          this.loginFormGroup.updateValueAndValidity();

          console.error(err);
        });
    }
  }

  cancelLogin(): void {
    this.router.navigate(['/welcome-agent']);
  }

  //show border when tab on link for accessibility
  linkTabAccessibility(event): void {
    event.style.outline = "1px solid black";
  }

  linkBlurAccessibility(event): void {
    event.style.outline = "";
  }

  //accessibility -- link error msg to form field
  errorStudentNumberAccessibility(): string {
    if (this.isFormSubmitted && this.loginFormGroup.get('loginId').errors && this.loginFormGroup.get('loginId').errors.required) {
      return "requireStudentNo";
    } else if (this.isFormSubmitted && this.loginFormGroup.get('loginId').errors &&
      (this.loginFormGroup.get('loginId').errors.notAuthorized ||
        this.loginFormGroup.get('loginId').errors.notConfirmed ||
        this.loginFormGroup.get('loginId').errors.generalUnAuthorized)) {
      return "loginErrorMsg";
    } else {
      return "";
    }
  }

  errorPasswordAccessibility(): string {
    if (this.isFormSubmitted && this.loginFormGroup.get('password').errors && this.loginFormGroup.get('password').errors.required) {
      return "requirePassword";
    } else if (this.isFormSubmitted && this.loginFormGroup.get('password').errors &&
      (this.loginFormGroup.get('password').errors.notAuthorized ||
        this.loginFormGroup.get('password').errors.notConfirmed ||
        this.loginFormGroup.get('password').errors.generalUnAuthorized)) {
      return "passwordErrorMsg";
    } else {
      return "";
    }
  }
}
